import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dismissToast, subscribeToasts, toastDuration, type ToastItem } from '../lib/toast';

/**
 * 저장·등록 결과 알림 표시(#390). 화면 위 가운데 · 팝업(z-50)·로딩 막대(z-60)보다 위.
 * 글자 수에 맞춰 사라지고, 마우스를 올리면 멈춘다. × 로 바로 닫는다.
 */
export default function Toaster() {
  const [list, setList] = useState<ToastItem[]>([]);
  useEffect(() => subscribeToasts(setList), []);
  if (!list.length) return null;
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[80] flex flex-col items-center gap-2 px-3 sm:top-5"
      role="status" aria-live="polite">
      {list.map((t) => <ToastCard key={t.id} item={t} />)}
    </div>,
    document.body,
  );
}

const TONE: Record<ToastItem['kind'], { box: string; icon: string; mark: string }> = {
  ok: { box: 'border-emerald-300', icon: 'bg-emerald-600 text-white', mark: '✓' },
  warn: { box: 'border-amber-300', icon: 'bg-amber-500 text-white', mark: '!' },
  err: { box: 'border-red-300', icon: 'bg-red-600 text-white', mark: '!' },
};

function ToastCard({ item }: { item: ToastItem }) {
  const [hover, setHover] = useState(false);
  const left = useRef(toastDuration(item.text));
  useEffect(() => {
    if (hover) return;
    const started = Date.now();
    const timer = window.setTimeout(() => dismissToast(item.id), left.current);
    return () => {
      window.clearTimeout(timer);
      left.current = Math.max(1500, left.current - (Date.now() - started));
    };
  }, [hover, item.id]);
  const tone = TONE[item.kind];
  return (
    <div
      className={`pointer-events-auto flex w-full max-w-lg animate-fade-in items-start gap-3 rounded-xl border-2 bg-surface px-4 py-3 shadow-xl ${tone.box}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-bold ${tone.icon}`}>
        {tone.mark}
      </span>
      <p className="flex-1 whitespace-pre-line break-keep text-[15px] font-medium leading-6 text-ink">{item.text}</p>
      <button type="button" className="shrink-0 rounded px-1 text-lg leading-6 text-ink-faint hover:text-ink"
        aria-label="알림 닫기" onClick={() => dismissToast(item.id)}>
        ×
      </button>
    </div>
  );
}
